import { isTenantFeatureActive } from '~/composables/useTenant';

const PageSettings = () => import('~/pages/settings/PageSettings.vue').then((m) => m.default || m);
const PageFactories = () => import('~/pages/settings/production/PageFactories.vue').then((m) => m.default || m);
const PageSite = () => import('~/pages/settings/production/PageSite.vue').then((m) => m.default || m);
const PageArea = () => import('~/pages/settings/production/PageArea.vue').then((m) => m.default || m);
const PageWorkCenter = () => import('~/pages/settings/production/PageWorkCenter.vue').then((m) => m.default || m);
const PageUsers = () => import('~/pages/settings/staff/PageUsers.vue').then((m) => m.default || m);
const PageDevices = () => import('~/pages/settings/production/PageDevices.vue').then((m) => m.default || m);
const PageDevice = () => import('~/pages/settings/production/PageDevice.vue').then((m) => m.default || m);
const PageEquipments = () => import('./PageEquipments.vue');
const PageProducts = () => import('~/pages/settings/job/PageProducts.vue').then((m) => m.default || m);
const PageDowntimeCauses = () => import('~/pages/settings/production/PageDowntimeCauses.vue').then((m) => m.default || m);
const PageRecurringEvents = () => import('~/pages/settings/times/PageRecurringEvents.vue').then((m) => m.default || m);
const PageMachine = () => import('~/pages/settings/production/PageMachine.vue').then((m) => m.default || m);
const PageMachines = () => import('~/pages/settings/production/PageMachines.vue').then((m) => m.default || m);
const PageMachinesGroups = () => import('~/pages/settings/production/PageMachinesGroups.vue').then((m) => m.default || m);
const PageDataExport = () => import('~/pages/settings/production/PageDataExport.vue').then((m) => m.default || m);
const PageDataExportForm = () => import('~/pages/settings/production/PageDataExportForm.vue').then((m) => m.default || m);
const PageAssignments = () => import('~/pages/settings/staff/PageAssignments.vue').then((m) => m.default || m);
const PageMyProfile = () => import('~/pages/PageMyProfile.vue').then((m) => m.default || m);
const PageNewFeatures = () => import('~/pages/PageNewFeatures.vue').then((m) => m.default || m);
const PageMyProfilePreferences = () => import('~/pages/PageMyProfilePreferences.vue').then((m) => m.default || m);
const PageMyProfileAccess = () => import('~/pages/PageMyProfileAccess.vue').then((m) => m.default || m);
const PageNotifications = () => import('~/pages/settings/staff/PageNotifications.vue').then((m) => m.default || m);
const PageClosingTimes = () => import('~/pages/settings/times/PageClosingTimes.vue').then((m) => m.default || m);
const PageCalendarExceptions = () => import('~/pages/settings/times/PageCalendarExceptions.vue').then((m) => m.default || m);
const PageOpeningTimes = () => import('~/pages/settings/times/PageOpeningTimes.vue').then((m) => m.default || m);
const PageSchedules = () => import('~/pages/settings/PageSchedules.vue').then((m) => m.default || m);
const PageEquipmentSchedules = () => import('~/pages/settings/PageEquipmentSchedules.vue').then((m) => m.default || m);
const PageScheduleCoverage = () => import('~/pages/settings/PageScheduleCoverage.vue').then((m) => m.default || m);
const PagePlanningOptions = () => import('~/pages/settings/job/PagePlanningOptions.vue').then((m) => m.default || m);
const PageAlerts = () => import('~/pages/settings/production/PageAlerts.vue').then((m) => m.default || m);
const PageMachineObjectives = () => import('~/pages/settings/production/PageMachineObjectives.vue').then((m) => m.default || m);
const PageExecutiveReport = () => import('~/pages/settings/production/PageExecutiveReport.vue').then((m) => m.default || m);
const PageExportSecurity = () => import('~/pages/settings/security/PageExportSecurity.vue').then((m) => m.default || m);
const PageApiTokens = () => import('~/pages/settings/integrations/PageApiTokens.vue').then((m) => m.default || m);
const PageApiTokensUsage = () => import('~/pages/settings/integrations/PageApiTokensUsage.vue').then((m) => m.default || m);
const PageSsoSettings = () => import('~/pages/settings/integrations/PageSsoSettings.vue').then((m) => m.default || m);
const PageLanguages = () => import('~/pages/settings/PageLanguages.vue').then((m) => m.default || m);

const FormFactory = () => import('~/pages/settings/production/components/factories/FormFactory.vue');
const FormArea = () => import('~/pages/settings/production/components/areas/FormArea.vue');
const FormWorkCenter = () => import('~/pages/settings/production/components/work-centers/FormWorkCenter.vue');
const WizardMachine = () => import('~/pages/settings/production/components/machines/WizardMachine.vue');

// ISA-95 equipments area, in both feature flag states. Consultation only: every
// role has View + Read on WorkCenters (operator included, see
// config/keyprodpermissions.php), so the default View check applies here. The
// "/edit" child routes carry their own actionPermission: 2 on their own
// resource, which is what actually gates writing.
const ISA95_EQUIPMENTS_META = {
  resource: 'WorkCenters',
};

/**
 * Route guard keeping a screen closed while its tenant feature flag is lowered: the tab and
 * the drawer entry hide themselves through meta.featureFlag, this covers a typed or bookmarked URL.
 * @param {string} featureFlag - The flag key IndexController injects into window.tenant.
 * @returns {Function} - A beforeEnter guard sending back to the settings home.
 */
const requireTenantFeature = (featureFlag) => (to, from, next) => (
  isTenantFeatureActive(featureFlag) ? next() : next({ name: 'settings' })
);

const settingsRoutes = [
  {
    path: '/settings',
    component: PageSettings,
    name: 'settings',
    meta: {
      resource: 'Settings',
      breadcrumb: 'user.settings',
    },
    children: [
      {
        path: 'devices',
        name: 'settings.devices',
        component: PageDevices,
        meta: {
          resource: 'Devices',
          breadcrumb: ['navigation.drawer.settings.label.organization', 'navigation.drawer.settings.label.devices'],
          title: 'navigation.drawer.settings.label.devices',
          subtitle: 'navigation.drawer.settings.subtitle.devices',
        },
      },
      {
        path: 'devices/:id',
        name: 'settings.device',
        component: PageDevice,
        meta: {
          resource: 'Devices',
          fillHeight: true,
          breadcrumb: ['navigation.drawer.settings.label.organization', 'navigation.drawer.settings.label.devices'],
        },
      },
      {
        path: 'users',
        name: 'settings.users',
        component: PageUsers,
        meta: {
          resource: 'Users',
          breadcrumb: ['navigation.drawer.settings.label.organization', 'navigation.drawer.settings.label.users'],
          tabGroup: 'staff',
          tabOrder: 0,
          title: 'settings.staff.users',
          subtitle: 'navigation.drawer.settings.subtitle.users',
        },
      },
      {
        path: 'languages',
        name: 'settings.languages',
        component: PageLanguages,
        meta: {
          resource: 'Languages',
          breadcrumb: ['navigation.drawer.settings.label.organization', 'navigation.drawer.settings.label.languages'],
          title: 'navigation.drawer.settings.label.languages',
          subtitle: 'navigation.drawer.settings.subtitle.languages',
        },
      },
      {
        path: 'assignments',
        name: 'settings.assignments',
        component: PageAssignments,
        meta: {
          resource: 'Assignments',
          breadcrumb: ['navigation.drawer.settings.label.organization', 'assignment.assignments'],
          tabGroup: 'staff',
          tabOrder: 1,
          title: 'navigation.drawer.settings.label.assignments',
          subtitle: 'navigation.drawer.settings.subtitle.users',
        },
      },
      {
        path: 'notifications',
        name: 'settings.notifications',
        component: PageNotifications,
        meta: {
          resource: 'Notifications',
          breadcrumb: ['navigation.drawer.settings.label.organization', 'notification.notifications'],
          tabGroup: 'staff',
          tabOrder: 2,
          title: 'navigation.drawer.settings.label.notifications',
          subtitle: 'navigation.drawer.settings.subtitle.users',
        },
      },
      /*
       * Integrations, not security: an administrator wiring up their IT looks for "integration".
       * The drawer entry is what leads here, and it is the place the API documentation link and
       * the future connectors will join.
       *
       * First tab of the `integrations` group, behind the `externalApi` tenant flag like the SSO
       * tab beside it is behind `sso`: the drawer entry opens on whichever of the two is open.
       */
      {
        path: 'api-tokens',
        name: 'settings.apiTokens',
        component: PageApiTokens,
        beforeEnter: requireTenantFeature('externalApi'),
        meta: {
          title: 'settings.integrations.apiTokens.apiTokens',
          subtitle: 'navigation.drawer.settings.subtitle.integrations',
          resource: 'ApiTokens',
          tabGroup: 'integrations',
          tabOrder: 0,
          featureFlag: 'externalApi',
          icon: 'vpn_key no-fill',
          breadcrumb: ['navigation.drawer.settings.label.organization', 'navigation.drawer.settings.label.integrations', 'settings.integrations.apiTokens.apiTokens'],
        },
      },
      /*
       * Hidden on purpose: no `tabGroup`, so no tab of the integrations group lists it, and no
       * drawer entry. Reached by its URL only, with the same permission as the token list.
       * `fillHeight` lets the chart take the whole height of the screen.
       */
      {
        path: 'api-tokens-usage',
        name: 'settings.apiTokensUsage',
        component: PageApiTokensUsage,
        beforeEnter: requireTenantFeature('externalApi'),
        meta: {
          title: 'settings.integrations.apiTokensUsage.title',
          subtitle: 'navigation.drawer.settings.subtitle.integrations',
          resource: 'ApiTokens',
          fillHeight: true,
          featureFlag: 'externalApi',
          breadcrumb: ['navigation.drawer.settings.label.organization', 'navigation.drawer.settings.label.integrations', 'settings.integrations.apiTokensUsage.title'],
        },
      },
      /*
       * Sous Intégrations, et non sous Utilisateurs : déclarer l'annuaire de son organisation
       * est un geste de branchement du SI, pas d'administration des comptes. Qui le cherche
       * pense « intégration », comme pour les autres logiciels qu'il relie à Keyprod.
       *
       * Derrière le drapeau `sso` : l'écran n'existe que pour un tenant dont la surface a été
       * ouverte. Le drapeau gouverne l'onglet, les identifiants saisis ici gouvernent la
       * possibilité de pointer un utilisateur vers l'annuaire.
       */
      {
        path: 'sso',
        name: 'settings.sso',
        component: PageSsoSettings,
        beforeEnter: requireTenantFeature('sso'),
        meta: {
          resource: 'SsoSettings',
          breadcrumb: ['navigation.drawer.settings.label.organization', 'navigation.drawer.settings.label.integrations', 'settings.integrations.sso.title'],
          tabGroup: 'integrations',
          tabOrder: 1,
          featureFlag: 'sso',
          title: 'settings.integrations.sso.title',
          subtitle: 'navigation.drawer.settings.subtitle.integrations',
        },
      },
      {
        path: 'personalization',
        name: 'settings.personalization',
        meta: {
        },
        children: [
          {
            name: 'settings.personalization.themes',
            path: 'themes',
            component: () => import('~/pages/settings/personalization/PageThemes.vue'),
            meta: {
              resource: 'Themes',
              tabGroup: 'personalization',
              breadcrumb: ['navigation.drawer.settings.label.organization', 'navigation.drawer.settings.label.environmentAppearance', 'navigation.drawer.settings.label.indicatorsAndEventsColors'],
              title: 'navigation.drawer.settings.label.indicatorsAndEventsColors',
              subtitle: 'navigation.drawer.settings.subtitle.personalization',
            },
          },
        ],
      },
      {
        path: 'security',
        name: 'settings.security',
        component: PageExportSecurity,
        meta: {
          title: 'settings.security.exportSecurity.exportSecurity',
          subtitle: 'navigation.drawer.settings.subtitle.security',
          resource: 'WidgetExports',
          tabGroup: 'security',
          tabOrder: 0,
          breadcrumb: ['navigation.drawer.settings.label.operations', 'navigation.drawer.settings.label.security', 'settings.security.exportSecurity.exportSecurity'],
        },
      },
      {
        path: 'newFeatures',
        name: 'settings.newFeatures',
        meta: {
          resource: 'NewFeatures',
          title: 'navigation.drawer.settings.label.newFeatures',
          subtitle: 'navigation.drawer.settings.subtitle.newFeatures',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'navigation.drawer.settings.label.newFeatures'],
        },
        component: PageNewFeatures,
      },
      {
        path: 'equipments',
        name: 'settings.equipments',
        component: PageEquipments,
        meta: {
          title: 'navigation.drawer.settings.label.equipment',
          subtitle: 'navigation.drawer.settings.subtitle.equipment',
          ...ISA95_EQUIPMENTS_META,
        },
        children: [
          {
            path: ':id',
            name: 'settings.site',
            component: PageSite,
            meta: ISA95_EQUIPMENTS_META,
            children: [
              {
                path: 'edit',
                name: 'settings.site.edit',
                component: FormFactory,
                meta: {
                  resource: 'Factories',
                  actionPermission: 2,
                },
              },
            ],
          },
          {
            path: 'areas/:id',
            name: 'settings.area',
            component: PageArea,
            meta: ISA95_EQUIPMENTS_META,
            children: [
              {
                path: 'edit',
                name: 'settings.area.edit',
                component: FormArea,
                meta: {
                  resource: 'Areas',
                  actionPermission: 2,
                },
              },
            ],
          },
          {
            path: 'workcenters/:id',
            name: 'settings.workcenter',
            component: PageWorkCenter,
            meta: ISA95_EQUIPMENTS_META,
            children: [
              {
                path: 'edit',
                name: 'settings.workcenter.edit',
                component: FormWorkCenter,
                meta: {
                  resource: 'WorkCenters',
                  actionPermission: 2,
                },
              },
            ],
          },
          {
            path: 'machines/:id',
            name: 'settings.machine',
            component: PageMachine,
            meta: ISA95_EQUIPMENTS_META,
            children: [
              {
                path: 'edit',
                name: 'settings.machine.edit',
                component: WizardMachine,
                meta: {
                  resource: 'Machines',
                  actionPermission: 2,
                },
              },
            ],
          },
        ],
      },
      {
        path: 'factories',
        name: 'settings.factories',
        component: PageFactories,
        meta: {
          resource: 'Factories',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'navigation.drawer.settings.label.equipment', 'navigation.drawer.settings.label.factories'],
          tabGroup: 'equipments',
          tabOrder: 0,
          title: 'navigation.drawer.settings.label.factories',
          subtitle: 'navigation.drawer.settings.subtitle.equipment',
          icon: 'factory no-fill',
        },
      },
      {
        path: 'groups/machines',
        name: 'machine.group.machineGroups',
        component: PageMachinesGroups,
        meta: {
          resource: 'MachineGroups',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'navigation.drawer.settings.label.equipment', 'machine.group.machineGroups'],
          tabGroup: 'equipments',
          tabOrder: 2,
          title: 'machine.group.machineGroups',
          subtitle: 'navigation.drawer.settings.subtitle.equipment',
          icon: 'communities no-fill',
        },
      },
      {
        path: 'machines',
        name: 'settings.machines',
        component: PageMachines,
        meta: {
          resource: 'Machines',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'navigation.drawer.settings.label.equipment', 'machine.machines'],
          tabGroup: 'equipments',
          tabOrder: 3,
          title: 'navigation.drawer.settings.label.parkMachines',
          subtitle: 'navigation.drawer.settings.subtitle.equipment',
          icon: 'conveyor_belt no-fill',
        },
      },
      {
        path: 'openingTimes',
        name: 'settings.openingTimes',
        component: PageOpeningTimes,
        meta: {
          resource: 'OpeningTimes',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'navigation.drawer.settings.label.time', 'openingTime.openingTimes'],
          tabGroup: 'times',
          tabOrder: 0,
          title: 'navigation.drawer.settings.label.openingTimes',
          subtitle: 'navigation.drawer.settings.subtitle.times',
        },
      },
      {
        path: 'closingTimes',
        name: 'settings.closingTimes',
        component: PageClosingTimes,
        meta: {
          resource: 'OpeningTimes',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'navigation.drawer.settings.label.time', 'closingTime.closingTimes'],
          tabGroup: 'times',
          tabOrder: 1,
          title: 'navigation.drawer.settings.label.closingTimes',
          subtitle: 'navigation.drawer.settings.subtitle.times',
        },
      },
      {
        path: 'recurringEvents',
        name: 'settings.recurringEvents',
        component: PageRecurringEvents,
        meta: {
          resource: 'RecurringEvents',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'navigation.drawer.settings.label.time', 'recurringEvent.recurringEvents'],
          tabGroup: 'times',
          tabOrder: 2,
          icon: 'free_cancellation no-fill',
          title: 'navigation.drawer.settings.label.recurringEvents',
          subtitle: 'navigation.drawer.settings.subtitle.times',
        },
      },
      {
        path: 'calendarExceptions/:id?',
        name: 'settings.calendarExceptions',
        component: PageCalendarExceptions,
        meta: {
          resource: 'Schedules',
          breadcrumb: ['navigation.drawer.settings.label.schedulesAndCalendars', 'navigation.drawer.settings.label.calendarExceptions'],
          tabGroup: 'schedules',
          tabOrder: 3,
          icon: 'event_busy no-fill',
          title: 'navigation.drawer.settings.label.calendarExceptions',
          subtitle: 'navigation.drawer.settings.subtitle.calendarExceptions',
        },
      },
      {
        path: 'siteClosingTimes',
        name: 'settings.schedules.siteClosingTimes',
        component: () => import('./schedules/PageSiteClosingTimes.vue'),
        meta: {
          resource: 'Schedules',
          breadcrumb: ['navigation.drawer.settings.label.schedulesAndCalendars', 'settings.schedules.tabs.siteClosingTimes'],
          tabGroup: 'schedules',
          tabOrder: 4,
          icon: 'lock no-fill',
          title: 'settings.schedules.tabs.siteClosingTimes',
          subtitle: 'navigation.drawer.settings.subtitle.siteClosingTimes',
        },
      },
      {
        path: 'scheduleCoverage',
        name: 'settings.schedules.coverage',
        component: PageScheduleCoverage,
        meta: {
          resource: 'Schedules',
          breadcrumb: [
            'navigation.drawer.settings.label.schedulesAndCalendars',
            'settings.schedules.tabs.coverage',
          ],
          tabGroup: 'schedules',
          tabOrder: 5,
          icon: 'fact_check no-fill',
          title: 'settings.schedules.tabs.coverage',
          subtitle: 'navigation.drawer.settings.subtitle.scheduleCoverage',
        },
      },
      {
        path: 'downtimeCauses',
        name: 'settings.downtimeCauses',
        component: PageDowntimeCauses,
        meta: {
          resource: 'DowntimeCauses',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'downtimeCause.downtimeCauses'],
          tabGroup: 'downtimeCauses',
          icon: 'report no-fill',
          tabOrder: 0,
          title: 'navigation.drawer.settings.label.downtimeCauses',
          subtitle: 'navigation.drawer.settings.subtitle.downtimeCauses',
        },
      },
      {
        path: 'downtimeCauses/recurringEvents',
        name: 'settings.downtimeCauses.recurringEvents',
        component: PageRecurringEvents,
        meta: {
          resource: 'RecurringEvents',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'downtimeCause.downtimeCauses', 'recurringEvent.recurringEvents'],
          tabGroup: 'downtimeCauses',
          tabOrder: 1,
          icon: 'free_cancellation no-fill',
          title: 'navigation.drawer.settings.label.recurringEvents',
          subtitle: 'navigation.drawer.settings.subtitle.recurringEvents',
        },
      },
      {
        path: 'machinesObjectives',
        name: 'settings.machinesObjectives',
        component: PageMachineObjectives,
        meta: {
          resource: 'MachinesObjectives',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'navigation.drawer.settings.label.machinesObjectives'],
          title: 'navigation.drawer.settings.label.machinesObjectives',
          subtitle: 'navigation.drawer.settings.subtitle.machinesObjectives',
        },
      },
      {
        // KP-19700: legacy misspelled URL kept for bookmarks. A redirect record rather than an
        // `alias`, which would duplicate this route in getRoutes() and so in the settings tab bar.
        path: 'equipementSchedules/:id?',
        redirect: (to) => ({ name: 'settings.schedules.equipmentSchedules', params: to.params, query: to.query }),
      },
      {
        path: 'equipmentSchedules/:id?',
        name: 'settings.schedules.equipmentSchedules',
        component: PageEquipmentSchedules,
        meta: {
          resource: 'Schedules',
          breadcrumb: ['navigation.drawer.settings.label.schedulesAndCalendars', 'navigation.drawer.settings.label.equipmentSchedules'],
          tabGroup: 'schedules',
          tabOrder: 1,
          icon: 'event_available no-fill',
          title: 'navigation.drawer.settings.label.equipmentSchedules',
          subtitle: 'navigation.drawer.settings.subtitle.equipmentSchedules',
        },
      },
      // {
      //   path: 'calendars',
      //   name: 'settings.schedules.calendar',
      //   component: PageSiteCalendar,
      //   meta: {
      //     resource: 'Schedules',
      //     breadcrumb: [
      //       'navigation.drawer.settings.label.schedulesAndCalendars',
      //       'navigation.drawer.settings.label.calendars',
      //     ],
      //     tabGroup: 'schedules',
      //     tabOrder: 2,
      //     title: 'navigation.drawer.settings.label.calendars',
      //     subtitle: 'navigation.drawer.settings.subtitle.schedules',
      //   },
      // },
      {
        path: 'schedules/:id?',
        name: 'settings.schedules',
        component: PageSchedules,
        meta: {
          resource: 'Schedules',
          breadcrumb: [
            'navigation.drawer.settings.label.schedulesAndCalendars',
            'navigation.drawer.settings.label.schedules',
          ],
          tabGroup: 'schedules',
          tabOrder: 0,
          icon: 'date_range no-fill',
          title: 'navigation.drawer.settings.label.schedules',
          subtitle: 'navigation.drawer.settings.subtitle.schedules',
        },
      },
      {
        path: 'products',
        name: 'settings.products',
        component: PageProducts,
        meta: {
          resource: 'Products',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'navigation.drawer.settings.label.production', 'product.products'],
          tabGroup: 'production',
          tabOrder: 0,
          title: 'navigation.drawer.settings.label.products',
          subtitle: 'navigation.drawer.settings.subtitle.production',
        },
      },
      {
        path: 'ManufacturingOrders',
        name: 'settings.manufacturing_orders',
        component: () => import('~/pages/settings/job/PageManufacturingOrders.vue')
          .then((m) => m.default || m),
        meta: {
          resource: 'ManufacturingOrders',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'navigation.drawer.settings.label.production', 'manufacturingOrder.manufacturingOrders'],
          tabGroup: 'production',
          tabOrder: 1,
          title: 'navigation.drawer.settings.label.manufacturingOrders',
          subtitle: 'navigation.drawer.settings.subtitle.production',
        },
      },
      {
        path: 'PlanningOptions',
        name: 'settings.planning_options',
        component: PagePlanningOptions,
        meta: {
          resource: 'PlanningOptions',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'navigation.drawer.settings.label.production', 'planning.planning'],
          tabGroup: 'production',
          tabOrder: 2,
          title: 'navigation.drawer.settings.label.planningOption',
          subtitle: 'navigation.drawer.settings.subtitle.production',
        },
      },
      {
        path: 'alerts/:id?',
        name: 'settings.alerts',
        component: PageAlerts,
        meta: {
          resource: 'Alerting',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'alert.alerts'],
          title: 'navigation.drawer.settings.label.alerts',
          subtitle: 'navigation.drawer.settings.subtitle.alerts',
        },
      },
      {
        path: 'dataExport',
        name: 'settings.dataExport',
        component: PageDataExport,
        meta: {
          resource: 'DataExport',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'dataExport.dataExport'],
          tabGroup: 'exports',
          tabOrder: 0,
          title: 'navigation.drawer.settings.label.dataExport',
          subtitle: 'navigation.drawer.settings.subtitle.export',
        },
        // TODO :: à supprimer
        children: [
          {
            path: ':id',
            name: 'settings.dataExport.form',
            component: PageDataExportForm,
          },
        ],
      },
      {
        path: 'executiveReports',
        name: 'settings.executiveReport',
        component: PageExecutiveReport,
        meta: {
          resource: 'ExecutiveReports',
          breadcrumb: ['navigation.drawer.settings.label.operations', 'navigation.drawer.settings.label.executiveReport'],
          tabGroup: 'exports',
          tabOrder: 1,
          title: 'navigation.drawer.settings.label.executiveReport',
          subtitle: 'navigation.drawer.settings.subtitle.export',

        },
      },
      {
        path: 'profile',
        name: 'myProfile',
        meta: {
          title: 'user.account',
          subtitle: 'menu.secondarySubtitle.profile',
          breadcrumb: ['common.settings', 'user.account', 'user.profile'],
        },
        component: PageMyProfile,
        children: [
          {
            path: 'preferences',
            name: 'myProfile.preferences',
            meta: {
              title: 'menu.secondary.myPreferences',
              subtitle: 'menu.secondarySubtitle.myPreferences',
              breadcrumb: ['menu.secondary.myPreferences'],
            },
            component: PageMyProfilePreferences,
          },
          {
            path: 'access',
            name: 'myProfile.access',
            meta: {
              title: 'user.access',
              subtitle: 'menu.secondarySubtitle.access',
              breadcrumb: ['user.access'],
            },
            component: PageMyProfileAccess,
          },
        ],
      },
    ],
  },
];

export default settingsRoutes;
